import { useEffect, useState } from 'react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  setCurrentPage: (page: number) => void
}

export default function Pagination({ currentPage, totalPages, setCurrentPage }: PaginationProps) {
  const [startPage, setStartPage] = useState(1)
  const [endPage, setEndPage] = useState(5)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 576)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handlePageClick = (page: number) => {
    if (page < 1 || page > totalPages) {
      return
    }
    setCurrentPage(page)
    if (page < startPage) {
      setStartPage(page)
      setEndPage(page + 4)
    } else if (page > endPage) {
      setEndPage(page)
      setStartPage(page - 4)
    }
  }

  const renderPageNumbers = () => {
    const pageNumbers: React.ReactNode[] = []
    const limiter = () => {
      if (isMobile) {
        return currentPage + 1
      }
      if (currentPage <= 2) {
        return currentPage + 3
      } else return currentPage + 2
    }

    const startPage = isMobile ? 1 : currentPage - (totalPages - currentPage < 2 ? 3 : 2)
    const endPage = limiter()

    for (let i = startPage; i <= endPage; i++) {
      i = i < 1 ? 1 : i
      if (i > totalPages) {
        break
      }

      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button type='button' className='page-link' onClick={() => handlePageClick(i)}>
            {i}
          </button>
        </li>
      )
    }

    return pageNumbers
  }

  const renderPreviousButton = () => {
    if (currentPage === 1) {
      return (
        <li className='page-item previous disabled'>
          <a href='#prev' className='page-link'>
            <i className='previous'></i>
          </a>
        </li>
      )
    }

    return (
      <li className='page-item previous'>
        <a href='#pprev' className='page-link' onClick={() => handlePageClick(currentPage - 1)}>
          <i className='previous'></i>
        </a>
      </li>
    )
  }

  const renderNextButton = () => {
    if (currentPage === totalPages) {
      return (
        <li className='page-item next disabled'>
          <a href='#next' className='page-link'>
            <i className='next'></i>
          </a>
        </li>
      )
    }

    return (
      <li className='page-item next'>
        <a href='#next' className='page-link' onClick={() => handlePageClick(currentPage + 1)}>
          <i className='next'></i>
        </a>
      </li>
    )
  }

  const renderFirstPageNumbers = () => {
    const pageNumbers: React.ReactNode[] = []

    const startPage = isMobile ? 1 : currentPage
    const endPage = isMobile ? 5 : currentPage + 2

    for (let i = startPage; i <= endPage && i <= totalPages; i++) {
      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button type='button' className='page-link' onClick={() => handlePageClick(i)}>
            {i}
          </button>
        </li>
      )
    }

    return pageNumbers
  }

  const renderLastPageNumbers = () => {
    const pageNumbers: React.ReactNode[] = []

    const startPage = isMobile ? totalPages - 2 : totalPages - 4
    const endPage = isMobile ? totalPages : totalPages

    for (let i = startPage; i <= endPage; i++) {
      if (i < 1) {
        continue
      }

      pageNumbers.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <button type='button' className='page-link' onClick={() => handlePageClick(i)}>
            {i}
          </button>
        </li>
      )
    }

    return pageNumbers
  }

  const renderPageNumbersBasedOnCurrentPage = () => {
    if (currentPage === 1) {
      return renderFirstPageNumbers()
    } else if (currentPage === totalPages) {
      return renderLastPageNumbers()
    } else {
      return renderPageNumbers()
    }
  }

  const renderLastPage = () => {
    if (currentPage > totalPages - 5 || isMobile) {
      return null
    }

    return (
      <>
        <li className='page-item'>
          <span className='page-link'>...</span>
        </li>
        <li className='page-item'>
          <button type='button' className='page-link' onClick={() => handlePageClick(totalPages)}>
            {totalPages}
          </button>
        </li>
      </>
    )
  }

  const renderFirstPage = () => {
    if (currentPage < 5 || isMobile) {
      return null
    }

    return (
      <>
        <li className='page-item'>
          <button type='button' className='page-link' onClick={() => handlePageClick(1)}>
            1
          </button>
        </li>
        <li className='page-item'>
          <span className='page-link'>...</span>
        </li>
      </>
    )
  }

  return (
    <>
      <ul className='pagination pagination-circle'>
        {renderPreviousButton()}
        {renderFirstPage()}
        {renderPageNumbersBasedOnCurrentPage()}
        {renderLastPage()}
        {renderNextButton()}
      </ul>
    </>
  )
}
